import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { MessageService } from 'primeng/api';
import { Clipboard } from '@angular/cdk/clipboard';
import { ApolloError } from '@apollo/client/errors';
import { GraphQLError } from 'graphql';
import canonicalize from 'canonicalize';


type HasuraErrorExtensionInternal = {
	arguments?: any[],
	error: {
		description: string | null,
		exec_status: string | null,
		hint: string | null,
		message: string,
		status_code: string | null
	}
};

type HasuraErrorExtension = {
	code: string,
	path: string,
	internal?: HasuraErrorExtensionInternal
};

@Injectable({
  providedIn: 'root'
})
export class CommonService {
	messageService: MessageService;
	auth: AuthService;
	clipboard: Clipboard;

  constructor(messageService: MessageService, auth: AuthService, clipboard: Clipboard) {
		this.messageService = messageService;
		this.auth = auth;
		this.clipboard = clipboard;
	}

	private mostRelevantErrorMessage(error: ApolloError): string {
		const graphQLError = error.graphQLErrors[0];
	
		if (!graphQLError) {
			return error.message;
		}
	
		const extensions =
			graphQLError.extensions as HasuraErrorExtension;
	
		return (
			extensions.internal?.error?.message ??
			graphQLError.message ??
			error.message
		);
	}

	queryErrorHandler(error: ApolloError) {
		const errorMsg = this.mostRelevantErrorMessage(error);
		console.log('There was an error with the request:', errorMsg);
		this.messageService.add({severity: 'error', summary: 'Error', detail: errorMsg, life: 10000});
	}

	mutationErrorHandler(error: ApolloError) {
		const errorMsg = this.mostRelevantErrorMessage(error);
		console.log('There was an error saving:', errorMsg);
		this.messageService.add({severity: 'error', summary: 'Error', detail: errorMsg, life: 20000});
	}

	simpleMessageErrorHandler(errorMsg: string) {
		console.log(errorMsg);
		this.messageService.add({severity: 'error', summary: 'Error', detail: errorMsg, life: 10000});
	}

	copyBearerTokenToClipboard() {
		this.auth.getAccessTokenSilently({cacheMode: 'cache-only'}).subscribe({
			next: (bearerToken: string) => {
				this.clipboard.copy(bearerToken);
				this.messageService.add({
					severity:'success',
					summary: 'Bearer token copied! Use this header format in the API to fetch your config:',
					detail: `Authorization: Bearer <BEARER TOKEN>`,
					life: 15000
				});
			}
		});
	}

	static friendlyVersionLabel(version: string): string {
		const startsWithNumber = /^\d/.test(version);
		// const startsWithVFollowedByNumber = /^(v|V)\d/.test(version);
		// const startsWithVersion = /^(v|V)ersion/.test(version);

		if (startsWithNumber) {
			return `v${version}`;
		}
		// else if (startsWithVFollowedByNumber) {
		// 	return version;
		// }
		// else if (startsWithVersion) {
		// 	return version;
		// }
		else {
			return `'${version}'`;
		}
	}

	public static async canonicalizeAndHashJson(value: unknown): Promise<string> {
		const canonicalJson = canonicalize(value);

		if (canonicalJson === undefined) {
			throw new Error('Could not canonicalize JSON');
		}

		const bytes = new TextEncoder().encode(canonicalJson);
		const digest = await crypto.subtle.digest('SHA-256', bytes);

		return Array.from(new Uint8Array(digest))
			.map(byte => byte.toString(16).padStart(2, '0'))
			.join('');
	}
}
